import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, FileText } from 'lucide-react';
import { Card } from '@/components/ui/card';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_f04fecbe-9399-4471-a47d-7dd5f6d0f107/artifacts/x9r7wgb6_App%20Development.png";

export const TermsPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FDF8F8] via-[#FFF5F5] to-[#FDF8F8]" data-testid="terms-page">
      <div className="max-w-3xl mx-auto px-6 py-8">
        <button 
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-500 hover:text-gray-900 transition-colors mb-8"
          data-testid="terms-back-btn"
        >
          <ChevronLeft className="w-5 h-5" />
          Back
        </button>

        <div className="flex items-center gap-4 mb-8">
          <div className="bg-white rounded-xl p-3 shadow-md">
            <img src={LOGO_URL} alt="AfriPerx" className="h-10" />
          </div>
          <div>
            <h1 className="font-syne text-2xl md:text-3xl font-bold text-gray-900">Terms and Conditions</h1>
            <p className="text-gray-500 text-sm">Last updated: December 2025</p>
          </div>
        </div>

        <Card className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 md:p-8 space-y-6">
          <div className="text-center border-b border-gray-200 pb-6">
            <h2 className="font-syne text-xl font-bold text-gray-900">AFRIPERX</h2>
            <p className="text-afriperx-gold font-medium text-sm">DRIVING THE PIONEERING SPIRIT</p>
            <p className="text-gray-600 mt-2 font-semibold">TERMS AND CONDITIONS OF USE</p>
          </div>

          <section className="space-y-2">
            <h3 className="font-syne text-lg font-bold text-gray-900">1. INTRODUCTION</h3>
            <p className="text-gray-700 leading-relaxed">Welcome to AfriPerx ("the Platform"), a fintech-enabled financial services ecosystem providing users with access to banking, payments, loyalty rewards, insurance, medical aid, and related value-added services.</p>
            <p className="text-gray-700 leading-relaxed">By registering, accessing, or using this Platform, you ("the User") agree to be legally bound by these Terms and Conditions.</p>
          </section>

          <section className="space-y-2">
            <h3 className="font-syne text-lg font-bold text-gray-900">2. NATURE OF SERVICES</h3>
            <p className="text-gray-700 leading-relaxed">AfriPerx is a technology platform provider that facilitates access to financial and lifestyle services through strategic partnerships.</p>
            <ul className="list-disc pl-6 text-gray-700 space-y-1">
              <li>AfriPerx does not operate as a licensed bank or financial institution.</li>
              <li>All regulated financial services, including banking and transaction processing, are provided by licensed third-party partners.</li>
              <li>AfriPerx integrates these services via secure APIs to enhance user experience and accessibility.</li>
            </ul>
          </section>

          <section className="space-y-2">
            <h3 className="font-syne text-lg font-bold text-gray-900">3. BANKING AND CARD SERVICES</h3>
            <p className="text-gray-700 leading-relaxed">The User acknowledges and agrees that:</p>
            <ul className="list-disc pl-6 text-gray-700 space-y-1">
              <li>Banking services and card issuance (including the Diamond Card) are provided in partnership with Bank Zero through an approved and licensed program.</li>
              <li>All deposits, withdrawals, payments, and transaction processing are executed and held by the partner banking institution, not AfriPerx.</li>
              <li>AfriPerx does not hold or control client funds at any time.</li>
            </ul>
          </section>

          <section className="space-y-2">
            <h3 className="font-syne text-lg font-bold text-gray-900">4. THIRD-PARTY PARTNERS</h3>
            <p className="text-gray-700 leading-relaxed">AfriPerx partners with various licensed service providers for:</p>
            <ul className="list-disc pl-6 text-gray-700 space-y-1">
              <li>Banking and payments</li>
              <li>Insurance products</li>
              <li>Medical aid services</li>
              <li>Loyalty and rewards programs</li>
              <li>Betting and gaming services (where applicable and legal)</li>
            </ul>
            <p className="text-gray-700 leading-relaxed font-medium mt-2">User Acknowledgement:</p>
            <ul className="list-disc pl-6 text-gray-700 space-y-1">
              <li>Each partner operates under its own regulatory licenses, compliance frameworks, and legal obligations.</li>
              <li>By using these services, the User agrees to the terms and conditions of each respective partner.</li>
            </ul>
          </section>

          <section className="space-y-2">
            <h3 className="font-syne text-lg font-bold text-gray-900">5. LIMITATION OF LIABILITY AND INDEMNITY</h3>
            <p className="text-gray-700 leading-relaxed">To the fullest extent permitted by law:</p>
            <h4 className="font-semibold text-gray-800 mt-2">5.1 No Liability for Partner Services</h4>
            <p className="text-gray-700 leading-relaxed">AfriPerx shall not be held liable for:</p>
            <ul className="list-disc pl-6 text-gray-700 space-y-1">
              <li>Any financial loss, damages, or disputes arising from services rendered by third-party partners</li>
              <li>Service interruptions, delays, or failures caused by partner systems</li>
              <li>Any regulatory or compliance breaches by partners</li>
            </ul>
            <h4 className="font-semibold text-gray-800 mt-2">5.2 Indemnification by User</h4>
            <p className="text-gray-700 leading-relaxed">The User agrees to fully indemnify and hold harmless AfriPerx, its directors, employees, and affiliates from any claims, damages, losses, or legal actions arising from use of third-party services, misconduct or misuse of the Platform, and disputes between the User and any partner.</p>
            <h4 className="font-semibold text-gray-800 mt-2">5.3 Partner Responsibility</h4>
            <p className="text-gray-700 leading-relaxed">All financial, insurance, medical, and betting services remain the sole responsibility of the respective licensed partner provider.</p>
          </section>

          <section className="space-y-2">
            <h3 className="font-syne text-lg font-bold text-gray-900">6. USER RESPONSIBILITIES</h3>
            <p className="text-gray-700 leading-relaxed">By using the Platform, the User agrees to:</p>
            <ul className="list-disc pl-6 text-gray-700 space-y-1">
              <li>Provide accurate and truthful information at all times</li>
              <li>Maintain the confidentiality of login credentials</li>
              <li>Use the Platform only for lawful purposes</li>
              <li>Comply with all applicable financial, gambling, and regulatory laws</li>
            </ul>
          </section>

          <section className="space-y-2">
            <h3 className="font-syne text-lg font-bold text-gray-900">7. LOYALTY AND REWARDS PROGRAM</h3>
            <ul className="list-disc pl-6 text-gray-700 space-y-1">
              <li>Loyalty points and rewards are issued based on activity and partner offerings.</li>
              <li>These points have no direct cash value unless stated otherwise and may expire or be modified at any time.</li>
              <li>Redemption is subject to partner availability and terms.</li>
            </ul>
          </section>

          <section className="space-y-2">
            <h3 className="font-syne text-lg font-bold text-gray-900">8. INSURANCE AND MEDICAL AID SERVICES</h3>
            <ul className="list-disc pl-6 text-gray-700 space-y-1">
              <li>All insurance and medical aid products are provided by licensed insurers and providers.</li>
              <li>AfriPerx does not underwrite policies and does not guarantee claims approval.</li>
              <li>Claims, disputes, and payouts are handled solely by the partner provider.</li>
            </ul>
          </section>

          <section className="space-y-2">
            <h3 className="font-syne text-lg font-bold text-gray-900">9. BETTING AND GAMING SERVICES</h3>
            <ul className="list-disc pl-6 text-gray-700 space-y-1">
              <li>Betting services are provided through licensed operators where legally permitted.</li>
              <li>The User confirms they meet the legal age requirement in their jurisdiction and understand the risks associated with gambling.</li>
              <li>AfriPerx is not responsible for losses incurred through betting activities.</li>
            </ul>
          </section>

          <section className="space-y-2">
            <h3 className="font-syne text-lg font-bold text-gray-900">10. DATA PRIVACY AND SECURITY</h3>
            <ul className="list-disc pl-6 text-gray-700 space-y-1">
              <li>AfriPerx uses secure technologies to protect user data.</li>
              <li>User data may be shared with partners strictly for service delivery and compliance (KYC, AML, FICA).</li>
              <li>By using the Platform, the User consents to such data sharing.</li>
            </ul>
          </section>

          <section className="space-y-2">
            <h3 className="font-syne text-lg font-bold text-gray-900">11. REGULATORY COMPLIANCE</h3>
            <ul className="list-disc pl-6 text-gray-700 space-y-1">
              <li>All financial services comply with applicable laws and regulations through licensed partners.</li>
              <li>Users may be required to complete identity verification (KYC) and provide supporting documentation.</li>
              <li>Failure to comply may result in account suspension or termination.</li>
            </ul>
          </section>

          <section className="space-y-2">
            <h3 className="font-syne text-lg font-bold text-gray-900">12. SERVICE AVAILABILITY</h3>
            <p className="text-gray-700 leading-relaxed">The Platform is provided "as is" and "as available". AfriPerx does not guarantee continuous uptime or error-free functionality.</p>
          </section>

          <section className="space-y-2">
            <h3 className="font-syne text-lg font-bold text-gray-900">13. TERMINATION</h3>
            <p className="text-gray-700 leading-relaxed">AfriPerx reserves the right to suspend or terminate accounts for breach of these Terms or suspicious or unlawful activity.</p>
          </section>

          <section className="space-y-2">
            <h3 className="font-syne text-lg font-bold text-gray-900">14. AMENDMENTS</h3>
            <p className="text-gray-700 leading-relaxed">AfriPerx may update these Terms at any time. Continued use of the Platform constitutes acceptance of updated Terms.</p>
          </section>

          <section className="space-y-2">
            <h3 className="font-syne text-lg font-bold text-gray-900">15. GOVERNING LAW</h3>
            <p className="text-gray-700 leading-relaxed">These Terms shall be governed by the laws of South Africa. Any disputes shall be subject to the jurisdiction of South African courts.</p>
          </section>

          <section className="space-y-2">
            <h3 className="font-syne text-lg font-bold text-gray-900">16. ACKNOWLEDGEMENT AND CONSENT</h3>
            <p className="text-gray-700 leading-relaxed">By clicking "Accept" or using the Platform, the User confirms that:</p>
            <ul className="list-disc pl-6 text-gray-700 space-y-1">
              <li>They understand that AfriPerx is a technology intermediary</li>
              <li>All financial services are provided by licensed third-party partners</li>
              <li>AfriPerx is indemnified from liability arising from partner services</li>
              <li>They accept all risks associated with financial transactions, insurance, and betting services</li>
            </ul>
          </section>

          <section className="space-y-2 border-t border-gray-200 pt-6">
            <h3 className="font-syne text-lg font-bold text-gray-900">17. CONTACT INFORMATION</h3>
            <p className="text-gray-700 leading-relaxed">For support or queries:</p>
            <p className="text-gray-700 font-medium">AfriPerx Support Team</p>
            <p className="text-afriperx-gold">Email: support@raoulcapital.online</p>
          </section>
        </Card>
      </div>
    </div>
  );
};
